/**
 * Employer — Interview Results Page
 *
 * Shows all AI interview sessions associated with the employer:
 * - Completed interviews (with video, transcript)
 * - Pending requests
 * - Ability to request a VA to complete an interview
 *
 * Route: /employer-dashboard/interviews
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, Clock, Search,
  Loader2, AlertCircle, Users, RefreshCw, Eye
} from 'lucide-react';
import InterviewVideoPlayer from '../../../components/interview/InterviewVideoPlayer';
import aiInterviewService from '../../../services/aiInterviewService';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscription } from '../../../contexts/SubscriptionContext';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Subscription guard - employer needs Pro+ to view interviews
const ALLOWED_TIERS = ['pro', 'premium', 'agency', 'enterprise'];

const EmployerInterviewResults = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const { subscription, loading: subscriptionLoading } = useSubscription();

  const subscriptionTier = subscription?.plan || 'free';

  const hasAccess = ALLOWED_TIERS.includes(subscriptionTier.toLowerCase());

  const [interviews,    setInterviews]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [viewingId,     setViewingId]     = useState(null);
  const [viewingSession, setViewingSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const loadInterviews = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await aiInterviewService.getEmployerInterviews(user.id);
      setInterviews(data);
    } catch (err) {
      setError('Failed to load interview results. Please try again.');
      console.error('[EmployerInterviews] loadInterviews error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const handleViewInterview = async (id) => {
    setViewingId(id);
    setSessionLoading(true);
    try {
      const session = await aiInterviewService.getSession(id);
      setViewingSession(session);
    } catch (err) {
      console.error('[EmployerInterviews] getSession error:', err);
    } finally {
      setSessionLoading(false);
    }
  };

  const filtered = interviews.filter(i => {
    const matchesSearch = !searchQuery.trim() ||
      (i.va?.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (i.job?.title     || '').toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  // Metrics
  const completed = interviews.filter(i => i.status === 'completed');

  // Wait for subscription data before evaluating access — prevents paywall flash
  if (subscriptionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Access guard
  if (!hasAccess) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[400px] text-center gap-4">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
          <Video className="w-7 h-7 text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Interview Results — Pro Feature</h2>
          <p className="text-gray-500 text-sm max-w-sm">
            Upgrade to a Pro or higher plan to request and view AI video interviews from VA candidates.
          </p>
        </div>
        <button
          onClick={() => navigate('/employer-dashboard/subscription')}
          className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 transition-colors"
        >
          Upgrade to Pro
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="w-5 h-5 text-emerald-600" />
            AI Interview Results
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Review video interviews from VA candidates.
          </p>
        </div>
        <button
          onClick={loadInterviews}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* ── Metrics row ── */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Total Interviews', value: interviews.length, icon: <Users className="w-4 h-4 text-emerald-600" /> },
          { label: 'Completed',        value: completed.length,  icon: <Video className="w-4 h-4 text-blue-600" />    },
        ].map(m => (
          <div key={m.label} className="bg-white border border-gray-100 rounded-xl p-4 text-center shadow-sm">
            <div className="flex justify-center mb-1">{m.icon}</div>
            <p className="text-2xl font-bold text-gray-900">{m.value}</p>
            <p className="text-xs text-gray-400">{m.label}</p>
          </div>
        ))}
      </div>

      {/* ── Search ── */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by VA name or job…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
        </div>
      </div>

      {/* ── List ── */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-7 h-7 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={loadInterviews} className="text-xs text-red-600 underline mt-1">Retry</button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-10 text-center border border-gray-200">
          <Video className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-600 text-sm">No interviews found</p>
          <p className="text-xs text-gray-400 mt-1">
            Request VA candidates to complete an AI interview from their profile or application.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(session => {
            const isCompleted = session.status === 'completed';

            return (
              <div
                key={session.id}
                className={`flex items-center bg-white border rounded-xl p-4 hover:border-emerald-200 transition-all ${isCompleted ? 'cursor-pointer' : ''} border-gray-200`}
                onClick={() => isCompleted && handleViewInterview(session.id)}
              >
                {/* VA avatar */}
                <div className="w-10 h-10 rounded-full overflow-hidden bg-emerald-100 flex items-center justify-center shrink-0">
                  {session.va?.profile_picture ? (
                    <img src={session.va.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-emerald-700">
                      {(session.va?.full_name || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {session.va?.full_name || 'Virtual Assistant'}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {session.job?.title || 'General VA Role'} · {formatDate(session.created_at)}
                  </p>
                </div>

                <div className="flex items-center gap-3 ml-3">
                  {isCompleted ? (
                    <Eye className="w-4 h-4 text-gray-400" />
                  ) : (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full font-medium">
                      <Clock className="w-3 h-3 inline mr-0.5" />
                      Pending
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── View Session Overlay ── */}
      {viewingId && (
        <div className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4" onClick={() => { setViewingId(null); setViewingSession(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Video className="w-4 h-4 text-emerald-600" />
                Interview Recording
              </h3>
              <button onClick={() => { setViewingId(null); setViewingSession(null); }} className="text-gray-400 hover:text-gray-700 text-xl">&times;</button>
            </div>
            <div className="p-5">
              {sessionLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                </div>
              ) : viewingSession ? (
                <InterviewVideoPlayer
                  videoUrl={viewingSession.video_url}
                  conversationLog={viewingSession.conversation_log || []}
                  vaName={viewingSession.va?.full_name}
                  canDownload={false}
                />
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">Failed to load session.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerInterviewResults;
